'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';

import { cn } from '@eliadmin/core/src/utils/style-utils';

/** Space between the sticky header (the `scroll-padding-top` of the page) and the sidebar. */
const OFFSET = 24;

/**
 * The sidebar of the article (Figma „Sidebar“ 250:15340) beside the text from 1024 px. It follows the
 * reading (the table of contents and its progress stay in view) only while it fits under the sticky
 * header – a taller sidebar scrolls with the page, so none of it is ever out of reach.
 */
export function StickyAside({ children, className }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLElement>(null);
  /** `top` of the sticky sidebar; null while it scrolls with the page. */
  const [top, setTop] = useState<number | null>(null);

  useEffect(() => {
    const aside = ref.current;
    if (!aside) return;
    const desktop = window.matchMedia('(min-width: 1024px)');
    const measure = () => {
      const header = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;
      const fits = desktop.matches && header + OFFSET + aside.offsetHeight <= window.innerHeight;
      setTop(fits ? header + OFFSET : null);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(aside);
    window.addEventListener('resize', measure);
    desktop.addEventListener('change', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
      desktop.removeEventListener('change', measure);
    };
  }, []);

  return (
    <aside
      ref={ref}
      style={top === null ? undefined : { top }}
      className={cn(top !== null && 'sticky', className)}
    >
      {children}
    </aside>
  );
}
